/**
 * Constructor method for creating instance of a header navigation item which can be either
 * a link to an external page, or a menuItem which opens a subNav
 *
 * Instance properties
 * - isHeaderNavItem : flag which denotes if the item exists in the top-level nav
 * - headerNav : reference to the controlling headerNav instance
 * - domNode : reference to domNode
 * - headerSubNav : reference to the sub-nav related to the item (if it exists)
 * - hasFocus : flag to identify current focus
 * - hasHover : flag to identify current hover
 *
 * @param {Node} domNode DomNode for the headerNavItem
 * @param {object} headerObj Instance of the controlling HeaderNav
 */

const HeaderNavItem = function (domNode, headerObj) {
  this.isHeaderNavItem = true;
  this.headerNav = headerObj;
  this.domNode = domNode;
  this.headerSubNav = false;

  this.hasFocus = false;
  this.hasHover = false;

  this.keyCode = Object.freeze({
    TAB: 9,
    RETURN: 13,
    ESC: 27,
    SPACE: 32,
    PAGEUP: 33,
    PAGEDOWN: 34,
    END: 35,
    HOME: 36,
    LEFT: 37,
    UP: 38,
    RIGHT: 39,
    DOWN: 40,
  });
};

/*
 * Initializer for a HeaderNav instance
 *
 * Sets all listeners and evaluates the existence of a "subNav" menu
 * by seeking dom elements which have matching IDs. If a subNav is found,
 * it is initialized and assigned to the instance of the NavItem
 *
 */
HeaderNavItem.prototype.init = function () {
  this.domNode.tabIndex = -1;

  this.domNode.addEventListener("keydown", this.handleKeydown.bind(this));
  this.domNode.addEventListener("focus", this.handleFocus.bind(this));
  this.domNode.addEventListener("blur", this.handleBlur.bind(this));
  this.domNode.addEventListener("mouseover", this.handleMouseover.bind(this));
  this.domNode.addEventListener("mouseout", this.handleMouseout.bind(this));

  // Initialize SubHeader
  const headerNavId = this.domNode.parentNode.id;
  let subNavId = `#sub-nav-${headerNavId}`;
  subNavId = subNavId.split(" ").join("\\ "); // Used to handle IDs with whitespace
  const subNavElement = this.headerNav.domNode.querySelector(subNavId);

  if (subNavElement) {
    this.headerSubNav = new HeaderSubNav(subNavElement, this);
    this.headerSubNav.init();
  }
};

HeaderNavItem.prototype.handleKeydown = function (event) {
  var char = event.key,
    flag = false;

  function isPrintableCharacter(str) {
    return str.length === 1 && str.match(/\S/);
  }

  switch (event.keyCode) {
    case this.keyCode.SPACE:
    case this.keyCode.RETURN:
    case this.keyCode.DOWN:
      if (this.headerSubNav) {
        this.headerSubNav.open();
        this.headerSubNav.setFocusToFirstItem();
        flag = true;
      }
      break;

    case this.keyCode.LEFT:
      this.headerNav.setFocusToPreviousItem(this);
      flag = true;
      break;

    case this.keyCode.RIGHT:
      this.headerNav.setFocusToNextItem(this);
      flag = true;
      break;

    case this.keyCode.UP:
      if (this.headerSubNav) {
        this.headerSubNav.open();
        this.headerSubNav.setFocusToLastItem();
        flag = true;
      }
      break;

    case this.keyCode.HOME:
    case this.keyCode.PAGEUP:
      this.headerNav.setFocusToFirstItem();
      flag = true;
      break;

    case this.keyCode.END:
    case this.keyCode.PAGEDOWN:
      this.headerNav.setFocusToLastItem();
      flag = true;
      break;

    case this.keyCode.TAB:
      this.headerSubNav.close(true);
      break;

    case this.keyCode.ESC:
      this.headerSubNav.close(true);
      break;

    default:
      if (isPrintableCharacter(char)) {
        this.headerNav.setFocusByFirstCharacter(this, char);
        flag = true;
      }
      break;
  }

  if (flag) {
    event.stopPropagation();
    event.preventDefault();
  }
};

HeaderNavItem.prototype.setExpanded = function (value) {
  if (value) {
    this.domNode.setAttribute("aria-expanded", "true");
  } else {
    this.domNode.setAttribute("aria-expanded", "false");
  }
};

HeaderNavItem.prototype.handleFocus = function (event) {
  this.headerNav.hasFocus = true;
};

HeaderNavItem.prototype.handleBlur = function (event) {
  this.headerNav.hasFocus = false;
};

HeaderNavItem.prototype.handleMouseover = function (event) {
  this.hasHover = true;
  if (this.headerSubNav) {
    this.headerSubNav.open();
  }
};

HeaderNavItem.prototype.handleMouseout = function (event) {
  this.hasHover = false;

  if (this.headerSubNav) {
    setTimeout(this.headerSubNav.close.bind(this.headerSubNav, false), 300);
  }
};
